import React, { useMemo, useState } from "react";
import { Package, Users, Briefcase, Search, ChevronRight, Link2Off } from "lucide-react";
import { Card } from "../ui/card";
import { useFm, fmtKg, fmtInt, fmtDate, Delta, Seg, SourceNote } from "./biShared";

const TABS = [
  { value: "products", label: "Prodotti", icon: Package },
  { value: "customers", label: "Clienti", icon: Users },
  { value: "agents", label: "Agenti", icon: Briefcase },
];
const SORTS = [{ value: "revenue", label: "€" }, { value: "kg", label: "Kg" }, { value: "ddt_count", label: "DDT" }];

const match = (r, q) => !q || [r.description, r.code, r.name, r.agent_name].some((s) => (s || "").toLowerCase().includes(q));

export default function BiBreakdown({ data, onPickProduct }) {
  const fm = useFm();
  const [tab, setTab] = useState("products");
  const [sort, setSort] = useState("revenue");
  const [q, setQ] = useState("");
  const [limit, setLimit] = useState(25);

  const rows = useMemo(() => {
    const list = (data[tab] || []).filter((r) => match(r, q.trim().toLowerCase()));
    return list.slice().sort((a, b) => (b[sort] || 0) - (a[sort] || 0));
  }, [data, tab, q, sort]);
  const total = useMemo(() => rows.reduce((s, r) => s + (r.revenue || 0), 0), [rows]);

  return (
    <Card className="bg-white border-stone-200 overflow-hidden" data-testid="bi-breakdown">
      <div className="p-3 sm:p-4 border-b border-stone-200 space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <Seg options={TABS.map((t) => ({ value: t.value, label: `${t.label} (${(data[t.value] || []).length})` }))} value={tab} onChange={(v) => { setTab(v); setLimit(25); }} testIdPrefix="bi-tab" wrap />
          <div className="flex items-center gap-1">
            <span className="text-xs text-stone-500 hidden sm:inline">Ordina</span>
            <Seg size="sm" options={SORTS} value={sort} onChange={setSort} testIdPrefix="bi-sort" />
            <SourceNote text={data.notes[tab]} testId={`bi-${tab}-source`} />
          </div>
        </div>
        <div className="relative">
          <Search className="w-4 h-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={tab === "products" ? "Cerca codice o descrizione..." : "Cerca nome..."} className="h-11 w-full pl-9 pr-3 rounded-xl border border-stone-300 text-sm" data-testid="bi-search" />
        </div>
      </div>

      {!rows.length && <p className="p-8 text-center text-sm text-stone-500 italic" data-testid="bi-breakdown-empty">Nessun dato DDT nel periodo selezionato</p>}

      <div className="divide-y divide-stone-100">
        {rows.slice(0, limit).map((r, i) => {
          const share = total > 0 ? (r.revenue / total) * 100 : 0;
          const key = r.code || r.key;
          const Row = tab === "products" ? "button" : "div";
          return (
            <Row key={key} onClick={tab === "products" ? () => onPickProduct(r) : undefined} className={`w-full text-left flex items-center gap-3 px-3 sm:px-4 py-3 min-h-[64px] ${tab === "products" ? "hover:bg-stone-50 active:bg-stone-100" : ""}`} data-testid={`bi-row-${tab}-${key}`}>
              <span className="w-6 text-xs text-stone-400 tabular-nums shrink-0">{i + 1}</span>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-stone-900 truncate">{tab === "products" ? r.description || r.code : r.name}</p>
                <p className="text-[11px] text-stone-500 truncate">
                  {tab === "products" && <><span className="font-mono">{r.code}</span> · {fmtInt(r.ddt_count)} DDT · {fmtInt(r.customers_count)} clienti{r.lots_count > 1 ? ` · ${r.lots_count} lotti` : ""}{r.avg_price != null ? ` · ${fm(r.avg_price)}/kg` : ""}</>}
                  {tab === "customers" && <>{fmtInt(r.ddt_count)} DDT · ultimo {fmtDate(r.last_date)} · {r.agent_name}{!r.linked && <span className="inline-flex items-center gap-0.5 ml-1 text-amber-700 shrink-0"><Link2Off className="w-3 h-3" /> non in anagrafica</span>}</>}
                  {tab === "agents" && <>{fmtInt(r.ddt_count)} DDT · {fmtInt(r.customers_count)} clienti serviti</>}
                </p>
                <div className="h-1.5 bg-stone-100 rounded-full mt-1.5 overflow-hidden"><div className="h-full bg-[#8A1C2A]/70 rounded-full" style={{ width: `${Math.max(1, share)}%` }} /></div>
              </div>
              <div className="text-right shrink-0">
                <p className="font-bold text-stone-900 tabular-nums">{fm(r.revenue)}</p>
                <p className="text-[11px] text-stone-500 tabular-nums">IVA incl. {fm(r.gross)}</p>
                <p className="text-xs text-stone-600 tabular-nums">{fmtKg(r.kg, 1)} kg</p>
                <Delta value={r.delta_revenue_pct} suffix="" className="mt-0.5" />
              </div>
              {tab === "products" && <ChevronRight className="w-4 h-4 text-stone-300 shrink-0" />}
            </Row>
          );
        })}
      </div>
      {rows.length > limit && (
        <button type="button" onClick={() => setLimit((l) => l + 50)} className="w-full h-12 text-sm font-semibold text-[#8A1C2A] hover:bg-stone-50 border-t border-stone-200" data-testid="bi-show-more">Mostra altri ({rows.length - limit})</button>
      )}
    </Card>
  );
}
